// src/chatbot/components/ChatbotMessage.tsx
import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { normalizeChatbotResponse } from "../utils/sanitizers";
import type { Branch, MenuItem } from "~/types/new_types";
import type { ChatbotMessage as ChatbotMessageType } from "../types/chatbot";

// Feature Components
import AudioPlayer from "./AudioPlayer";
import MessageAvatar from "./MessageAvatar";
import MessageBubble from "./MessageBubble";
import MessageTimestamp from "./MessageTimestamp";
import StreamingIndicator from "./StreamingIndicator";
import SuggestedItems from "./SuggestedItems";

interface ChatbotMessageProps {
  message: ChatbotMessageType;
  theme: Branch["theme"];
  onItemClick?: (item: MenuItem) => void;
}

/**
 * Hook to extract audio source from message data
 */
const useAudioSource = (message: ChatbotMessageType) => {
  return useMemo(() => {
    // Check for base64 audio in message
    if (message.audioBase64) {
      const normalizedType = message.contentType?.startsWith("audio/")
        ? message.contentType
        : message.contentType
        ? `audio/${message.contentType}`
        : "audio/wav";
      return {
        src: `data:${normalizedType};base64,${message.audioBase64}`,
        type: normalizedType,
      };
    }

    // Check for data URI in text
    if (message.text.startsWith("data:audio/")) {
      const match = message.text.match(/^data:([^;]+);base64,/);
      const inferredType = match?.[1] ?? "audio/webm";
      return {
        src: message.text,
        type: inferredType,
      };
    }

    // Check for voice message prefix
    if (message.text.startsWith("[VOICE_MESSAGE]:")) {
      return {
        src: `data:audio/webm;base64,${message.text.replace(
          "[VOICE_MESSAGE]:",
          ""
        )}`,
        type: "audio/webm",
      };
    }

    return null;
  }, [message.audioBase64, message.contentType, message.text]);
};

const ChatbotMessage: React.FC<ChatbotMessageProps> = ({
  message,
  theme,
  onItemClick,
}) => {
  const { i18n } = useTranslation();
  const audioSource = useAudioSource(message);
  const isVoiceMessage = Boolean(audioSource);
  const hasSuggestedItems =
    !message.isUser &&
    message.suggestedItems &&
    message.suggestedItems.length > 0;

  // Animation variants - removed x offset to prevent horizontal overflow
  const messageVariants = {
    hidden: {
      opacity: 0,
      y: 15,
      scale: 0.98,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring" as const,
        stiffness: 400,
        damping: 25,
      },
    },
  };

  return (
    <motion.div
      variants={messageVariants}
      initial="hidden"
      animate="visible"
      className={`flex gap-3 mb-4 ${
        message.isUser ? "justify-end" : "justify-start"
      }`}
    >
      {/* Bot Avatar (left side) */}
      {!message.isUser && <MessageAvatar isUser={false} theme={theme} />}

      {/* Message Content */}
      <div
        className={`max-w-[75%] min-w-0 ${
          message.isUser ? "items-end" : "items-start"
        } flex flex-col`}
      >
        {/* Message Bubble */}
        {isVoiceMessage && audioSource ? (
          <MessageBubble isUser={message.isUser} theme={theme}>
            <AudioPlayer
              audioSource={audioSource}
              theme={theme}
              isUser={message.isUser}
            />
          </MessageBubble>
        ) : (
          <MessageBubble isUser={message.isUser} theme={theme}>
            {String(normalizeChatbotResponse(message.text) || "")}
            {message.isStreaming && <StreamingIndicator theme={theme} />}
          </MessageBubble>
        )}

        {/* Suggested Items */}
        {hasSuggestedItems && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-3 w-full"
          >
            <SuggestedItems
              items={message.suggestedItems!}
              theme={theme}
              onItemClick={onItemClick}
            />
          </motion.div>
        )}

        {/* Timestamp */}
        <MessageTimestamp
          timestamp={message.timestamp}
          isUser={message.isUser}
          theme={theme}
        />
      </div>

      {/* User Avatar (right side) */}
      {message.isUser && <MessageAvatar isUser={true} theme={theme} />}
    </motion.div>
  );
};

export default ChatbotMessage;
